import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { usePathname } from "next/navigation";
import { createContext, useCallback, useEffect, useMemo, useRef } from "react";

export const ScrollLockContext = createContext<{
  lockScroll: (key: string, priority?: number) => void;
  unlockScroll: (key: string, priority?: number) => void;
}>({
  lockScroll: () => {},
  unlockScroll: () => {},
});

export function ScrollLockProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>): React.ReactNode {
  const currentPathname = usePathname();
  const stack = useRef<[string, number][]>([]);

  useEffect(() => {
    if (stack.current.length > 0) {
      enableBodyScroll(document.body);
      flushStack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPathname]);

  const flushStack = useCallback(() => {
    stack.current = [];
  }, []);

  const lockScroll = useCallback((key: string, priority?: number) => {
    stack.current.push([key, priority ?? 0]);
    disableBodyScroll(document.body, {
      reserveScrollBarGap: true,
    });
  }, []);

  const unlockScroll = useCallback((key: string, priority?: number) => {
    const length = stack.current.length;
    const lastItem = stack.current[length - 1];

    if (lastItem) {
      const [lastKey, lastPriority] = lastItem;
      if (lastKey === key && lastPriority === priority) {
        stack.current.pop();
        // only enable body scroll if the stack is empty
        if (stack.current.length === 0) {
          enableBodyScroll(document.body);
        }
        return;
      }
      if (priority && lastPriority < priority) {
        flushStack();
        enableBodyScroll(document.body);
        return;
      }
    }
  }, []);

  return (
    <ScrollLockContext.Provider
      value={useMemo(
        () => ({
          lockScroll,
          unlockScroll,
        }),
        [lockScroll, unlockScroll],
      )}
    >
      {children}
    </ScrollLockContext.Provider>
  );
}
